










































































































































































import { Component, Vue } from 'vue-property-decorator';
import { mdbBtn, mdbIcon, mdbTooltip, mdbProgress } from '@folky-tools/mdbvue';
import { VueGoodTable } from '@folky-tools/vue-good-table';
import { usersService, axiosService, authorizationManagerService, businessesService, userInvitationsService, notificationsService, appService } from '@/services';
import { UserActionLogInfo } from '@/models/users/user-logging/user-action-log-info';
import { UserActionType } from '@/models/users/user-logging/user-action-type';
import { UserActionEntityType } from '@/models/users/user-logging/user-action-entity-type';
import { UserActionScopeType } from '@/models/users/user-logging/user-action-scope-type';
import { Business } from '@/models/businesses/business';
import { EntityOwner } from '@/models/common/entity-owner';
import { Invitation } from '@/models/users/user-invitations/invitation';
import Swal from 'sweetalert2';
import { String } from 'typescript-string-operations';

@Component({
    components: {
        mdbBtn,
        mdbIcon,
        mdbTooltip,
        mdbProgress,
        VueGoodTable,
        FormModal: () => import('@/components/shared/FormModal.vue'),
        ChangeEntityOwner: () => import('@/components/shared/ChangeEntityOwner.vue'),
        AppSysBar: () => import('@/components/shared/AppSysBar.vue')
    }
})
export default class BusinessListView extends Vue {
    private businessPerPage: number = 20;
    private owner: EntityOwner = new EntityOwner();
    private showChangeOwner: boolean = false;
    private savingNewOwner: boolean = false;
    private sendingNewOwnerInvitation: boolean = false;
    private actualUserCanCreateBusiness: boolean = false;
    private rows: any[] = [];
    private columns: any[] = [];
    private totalRecords: number = 0;
    private isLoading: boolean = false;
    private deletingBusinessId: number = 0;
    private serverParams: any = {
        columnFilters: {
            tradeName: '',
            projectNames: ''
        },
        sort: [
            {
                field: 'id',
                type: 'desc'
            }
        ],
        page: 1,
        perPage: 20
    };

    private async beforeMount(): Promise<void> {
        await this.setColumns();
        await this.loadItems();
    }

    private async mounted(): Promise<void> {
        this.$root.$emit('change-active-page', 'views.businesses.businessListView.title');
        this.actualUserCanCreateBusiness = authorizationManagerService.canCreateBusiness;

        await usersService.logUserAction(
            new UserActionLogInfo({
                type: UserActionType.HasVisited,
                entity: UserActionEntityType.Businesses,
                scope: UserActionScopeType.List
            })
        );
    }

    private getQueryParams(sortOrder: any, currentPage: number, perPage: number): any {
        return {
            $skip: (currentPage - 1) * perPage,
            $top: perPage,
            $orderby: sortOrder && sortOrder.length === 1 ? `${sortOrder[0].field} ${sortOrder[0].type}` : null,
            $filter: this.getQueryFilterFromColumns(),
            projectNameFilter: this.serverParams.columnFilters['projectNames'].replace('\'', '\'\'')
        };
    }

    private updateParams(newProps: any): void {
        this.serverParams = { ...this.serverParams, ...newProps };
    }

    private async onPageChange(params: any): Promise<void> {
        this.updateParams({ page: params.currentPage });
        await this.loadItems();
    }

    private async onSortChange(params: any): Promise<void> {
        this.updateParams({
            sort: [
                {
                    type: params[0].type,
                    field: params[0].field
                }
            ]
        });
        await this.loadItems();
    }

    private async onColumnFilter(params: any): Promise<void> {
        this.serverParams.page = 1;
        this.updateParams(params);
        await this.loadItems();
    }

    private async onPerPageChange(params: any): Promise<void> {
        this.updateParams({ perPage: params.currentPerPage });
        await this.loadItems();
    }

    private async loadItems(): Promise<void> {
        this.isLoading = true;

        const response: any = await axiosService.instance.get('businesses/organization/query', {
            params: this.getQueryParams(this.serverParams.sort, this.serverParams.page, this.serverParams.perPage)
        });

        this.rows = [];

        this.totalRecords = response.data.count;
        response.data.items.forEach((item: any) => {
            return this.rows.push(new Business(item));
        });

        this.isLoading = false;
    }

    private getQueryFilterFromColumns(): string | null {
        let queryFilter = '';
        const logicalOperatorBetweenFilters = ' and ';

        for (const key of Object.keys(this.serverParams.columnFilters)) {
            if (this.serverParams.columnFilters[key]) {
                if (key === 'tradeName') {
                    queryFilter += `${logicalOperatorBetweenFilters}contains(tradeName, '${this.serverParams.columnFilters[key].replace('\'', '\'\'')}')`;
                }
            }
        }

        return queryFilter === '' ? null : queryFilter.substring(logicalOperatorBetweenFilters.length);
    }

    private setColumns(): void {
        this.columns = [
            { field: 'id', type: 'number', sortable: true },
            {
                field: 'tradeName',
                sortable: true,
                type: 'text',
                filterOptions: {
                    enabled: true,
                    placeholder: this.$t('common.placeholders.searchByName'),
                    filterValue: ''
                }
            },
            { field: 'ownerEmail', sortable: false, type: 'text' },
            {
                field: 'projectNames',
                sortable: false,
                type: 'text',
                filterOptions: {
                    enabled: true,
                    placeholder: this.$t('common.placeholders.searchByProject'),
                    filterValue: ''
                }
            }
        ];
        if (authorizationManagerService.isAdmin) {
            this.columns.push({ field: 'pricingLevel', sortable: false, type: 'number' });
        }
        this.columns.push({ field: 'isPublished', sortable: false });
        if (authorizationManagerService.isAdmin) {
            this.columns.push({ field: 'type', sortable: false, type: 'boolean' });
        }
        this.columns.push({ field: 'actions-slot', sortable: false });
    }

    private changeBusinessOwner(business: Business): void {
        this.owner = new EntityOwner({
            id: business.ownerId,
            email: business.ownerEmail,
            managedEntityId: business.id,
            managedEntityProjectIds: [business.projects[0].id]
        });

        this.showChangeOwner = true;
    }

    private async inviteBusinessOwner(owner: EntityOwner): Promise<void> {
        this.sendingNewOwnerInvitation = true;
        try {
            const currentApplication = appService.getCurrentApplication();
            const businessOwnerInvitation: Invitation = new Invitation({
                projectId: owner.managedEntityProjectIds[0],
                businessId: owner.managedEntityId,
                email: owner.email,
                destinationApplicationId: currentApplication.id,
                destinationApplicationClientId: currentApplication.clientId,
                destinationApplicationName: `${currentApplication.clientName} (${currentApplication.clientUri})`,
                destinationApplicationUri: currentApplication.clientUri
            });

            if ((await userInvitationsService.newInvitation(businessOwnerInvitation)) && (await businessesService.updateBusinessInvitedOwnerEmail(owner.managedEntityId, owner.email!))) {
                await this.loadItems();
                this.closeChangeBusinessOwner();

                notificationsService.showToastSuccessNotification('views.businesses.businessListView.notifications.invitationSent.successMessage');

                await usersService.logUserAction(
                    new UserActionLogInfo({
                        type: UserActionType.HasChanged,
                        entity: UserActionEntityType.Businesses,
                        scope: UserActionScopeType.Owner,
                        entityId: owner.managedEntityId
                    })
                );

                return;
            }

            notificationsService.showToastErrorNotification();
        } catch {
            notificationsService.showToastErrorNotification();
        } finally {
            this.sendingNewOwnerInvitation = false;
        }
    }

    private async saveBusinessNewOwner(): Promise<void> {
        this.savingNewOwner = true;

        if (await (this.$refs.changeEntityOwner as any).hasErrors()) {
            this.savingNewOwner = false;
            return;
        }

        try {
            if (await businessesService.updateBusinessOwner(this.owner.managedEntityId, this.owner.id, this.owner.email)) {
                await this.loadItems();
                this.closeChangeBusinessOwner();

                notificationsService.showToastSuccessNotification('views.businesses.businessListView.notifications.changeOwner.successMessage');

                await usersService.logUserAction(
                    new UserActionLogInfo({
                        type: UserActionType.HasChanged,
                        entity: UserActionEntityType.Businesses,
                        scope: UserActionScopeType.Owner,
                        entityId: this.owner.managedEntityId
                    })
                );

                return;
            }
            notificationsService.showToastErrorNotification();
        } catch {
            notificationsService.showToastErrorNotification();
        } finally {
            this.savingNewOwner = false;
        }
    }

    private closeChangeBusinessOwner(): void {
        this.showChangeOwner = false;
    }

    private async deleteBusiness(id: number, tradeName: string): Promise<void> {
        const questionResult = await Swal.fire({
            title: this.$t('views.businesses.businessListView.actions.delete.modal.title').toString(),
            html: '<h3>' + tradeName + '</h3>' + this.$t('views.businesses.businessListView.actions.delete.modal.text').toString(),
            type: 'warning',
            showCancelButton: true,
            reverseButtons: true,
            focusConfirm: false,
            focusCancel: true,
            confirmButtonText: this.$t('views.businesses.businessListView.actions.delete.modal.confirm.doConfirm').toString(),
            cancelButtonText: this.$t('views.businesses.businessListView.actions.delete.modal.doCancel').toString(),
            customClass: {
                confirmButton: 'btn btn-primary',
                cancelButton: 'btn btn-danger'
            },
            animation: false
        });

        if (questionResult.value) {
            this.deletingBusinessId = id;
            const deleteResponse: any = await businessesService.deleteBusiness(id);
            if (deleteResponse.result === true) {
                notificationsService.showToastSuccessNotification('views.businesses.businessListView.actions.delete.modal.confirm.successMessage');
                await this.loadItems();

                await usersService.logUserAction(
                    new UserActionLogInfo({
                        type: UserActionType.HasDeleted,
                        entity: UserActionEntityType.Businesses,
                        scope: UserActionScopeType.Delete,
                        entityId: id
                    })
                );
                this.deletingBusinessId = 0;

                return;
            } else if (deleteResponse.result === false) {
                if (deleteResponse.isPublishedWithSlug || deleteResponse.hasPublishedReferences || deleteResponse.hasAssociatedThreads) {
                    if (deleteResponse.isPublishedWithSlug && deleteResponse.hasPublishedReferences) {
                        notificationsService.showToastErrorNotification(String.Format(this.$t('views.businesses.businessListView.actions.delete.modal.confirm.errorMessage.hasPublishedReferences').toString(), deleteResponse.publishedReferencesCount));
                        this.deletingBusinessId = 0;
                        return;
                    } else if (deleteResponse.isPublishedWithSlug) {
                        notificationsService.showToastErrorNotification('views.businesses.businessListView.actions.delete.modal.confirm.errorMessage.isPublishedWithSlug');
                        this.deletingBusinessId = 0;
                        return;
                    } else if (deleteResponse.hasAssociatedThreads) {
                        notificationsService.showToastErrorNotification('views.businesses.businessListView.actions.delete.modal.confirm.errorMessage.hasActiveThreads');
                        this.deletingBusinessId = 0;
                        return;
                    }
                }
            }

            notificationsService.showToastErrorNotification();
        }
    }

    private async copyLinkToSmartAdminToClipboard(businessId: number): Promise<void> {
        await navigator.clipboard.writeText(`${process.env.VUE_APP_SMART_ADMIN_SHARE_URL}${businessId}`);
        notificationsService.showToastInfoNotification('El enalace para compartir ha sido copiado al portapapeles.');
    }
}
